import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLink from "../../components/Link/Link";
import ScrollAnimation from "../../components/ScrollAnimation/ScrollAnimation";
import { SEO } from "../../components/SEO";
import {
  ServiceRoot,
  ServiceContent,
  WhatWeDoContainer,
  WhatWeDoTitle,
  ServicesGrid,
  ServiceCard,
  ServiceCardHeader,
  ServiceNumber,
  ServiceCardTitle,
  ServiceCardDescription,
  IconContainer,
  WorkSection,
  WorkSectionHeader,
  TypeOfWorkTitle,
  SeeAllButton,
  ServiceCardsGrid,
  ProjectCountText,
  CardTitle,
  TypeOfWorkCard,
  MediaPartnerSection,
  MediaPartnerTitle,
  MediaPartnerContent,
  TitleContainer,
  MediaPartnerDescription,
  MediaPartnerImage1,
  MediaPartnerImage2,
  ScopeOfWorkSection,
  ScopeOfWorkTitle,
  ScopeOfWorkGrid,
  ScopeCard,
  ScopeCardTitle,
  ScopeImageContainer,
  WhatWeDoTab,
  WhatWeDoTabItem,
  CardSliderContainer,
  CardContainer,
  CardPageIndicator,
  CardIndicatorDot,
  ServiceDescription,
  VoucherWhatWeDoDescription
} from "./styles";

// 카드 데이터 배열 추가
const typeOfWorkCards = [
  {
    id: 0,
    title: "Brand Strategy",
    image: "/images/service/type-of-work/brand-strategy.svg",
    projectCount: "10 Projects",
    text: "<span>Full Funnel Marketing</span><span>Brand Build Up</span><span>Brand Sale Up</span><span>Total Solution</span>"
  },
  {
    id: 1,
    title: "Growth Marketing",
    image: "/images/service/type-of-work/growth-marketing.svg",
    projectCount: "18 Projects",
    text: "<span>Data Analytics</span><span>Channel Strategy</span><span>Media Operation</span><span>Sales Maximization</span>"
  },
  {
    id: 2,
    title: "UGC Marketing",
    image: "/images/service/type-of-work/ugc-marketing.svg",
    projectCount: "18 Projects",
    text: "<span>Influencer</span><span>SNS Power Page</span><span>Cafe / Community Viral</span><span>Secondary Utilization</span>"
  },
  {
    id: 3,
    title: "Content",
    image: "/images/service/type-of-work/content.svg",
    projectCount: "3 Projects",
    text: "<span>Video</span><span>Design</span><span>Photo</span><span>Copywriting</span>"
  }
];

const Service = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [whatWeDoTab, setWhatWeDoTab] = useState(tabParam === 'voucher' ? 1 : 0);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeScopeCard, setActiveScopeCard] = useState<number | null>(null);
  const [processSlide, setProcessSlide] = useState(0);
  const [activeWhatWeDo, setActiveWhatWeDo] = useState(0);
  const [activeVoucherCard, setActiveVoucherCard] = useState<number | null>(null);
  const navigate = useNavigate();
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const processContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigationIntentRef = useRef<number | null>(null);
  const lastFlipTimeRef = useRef<number>(0);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 1050;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // 모바일에서 go-to-list 클릭 시 바로 works 페이지로 이동
  const handleGoToListClick = (index: number) => (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    e.preventDefault(); // 기본 동작 방지
    
    const now = Date.now();
    
    if (activeCard === index) {
      // 카드가 뒤집힌 후 0.5초가 지났는지 확인
      if (now - lastFlipTimeRef.current >= 500) {
        // 이미 active 상태면 바로 이동
        const categoryMap: { [key: string]: string } = {
          'Brand Strategy': 'BrandStrategy',
          'Growth Marketing': 'GrowthMarketing',
          'UGC Marketing': 'UGCMarketing',
          'Content': 'Contents'
        };
        const category = categoryMap[typeOfWorkCards[index].title] || '';
        navigate(`/works?filter=${category}`);
        window.scrollTo(0, 0);
      }
    } else {
      // active 상태가 아니면 이동 의도를 저장하고 카드를 뒤집음
      navigationIntentRef.current = index;
      lastFlipTimeRef.current = now;
      handleCardClick(index);
    }
  };
  
  // 모바일에서는 토글, PC에서는 바로 이동
  const handleCardClick = (index: number) => {
    if (isMobile) {
      // 모바일에서는 카드 클릭 시 이미지/텍스트 뷰 토글
      setActiveCard(activeCard === index ? null : index);
      if (activeCard !== index) {
        lastFlipTimeRef.current = Date.now();
      }
    } else {
      // PC에서는 바로 이동
      const categoryMap: { [key: string]: string } = {
        'Brand Strategy': 'BrandStrategy',
        'Growth Marketing': 'GrowthMarketing',
        'UGC Marketing': 'UGCMarketing',
        'Content': 'Contents'
      };
      const category = categoryMap[typeOfWorkCards[index].title] || '';
      navigate(`/works?filter=${category}`);
      window.scrollTo(0, 0);
    }
  };
  
  // 카드 상태가 변경될 때마다 실행되는 useEffect
  useEffect(() => {
    if (activeCard !== null && navigationIntentRef.current === activeCard) {
      // 카드 애니메이션 시간을 고려한 지연 후 이동
      const timer = setTimeout(() => {
        const now = Date.now();
        // 카드가 뒤집힌 후 0.5초가 지났는지 확인
        if (now - lastFlipTimeRef.current >= 500) {
          const categoryMap: { [key: string]: string } = {
            'Brand Strategy': 'BrandStrategy',
            'Growth Marketing': 'GrowthMarketing',
            'UGC Marketing': 'UGCMarketing',
            'Content': 'Contents'
          };
          const category = categoryMap[typeOfWorkCards[activeCard].title] || '';
          navigate(`/works?filter=${category}`);
          window.scrollTo(0, 0);
          navigationIntentRef.current = null;
        }
      }, 300); // 애니메이션 시간에 맞춰 조정

      return () => clearTimeout(timer);
    }
    return () => {}; // 빈 cleanup 함수 반환
  }, [activeCard, navigate, typeOfWorkCards]);
  
  // 슬라이드 변경 처리
  const handleSlideChange = (index: number) => {
    // 여러 개 이동하는 경우 자연스러운 효과를 위해 애니메이션 시간 조정
    const currentIndex = currentSlide;
    const distance = Math.abs(index - currentIndex);
    
    setCurrentSlide(index);
    setActiveCard(null); // 슬라이드 변경 시 활성화된 카드 초기화
    
    if (cardContainerRef.current) {
      // 카드 너비 + 마진 오른쪽(20px)을 포함한 계산
      const cardWidth = 335; // 카드 너비 고정
      const cardMargin = 20; // 오른쪽 마진
      const fullCardWidth = cardWidth + cardMargin;
      
      // 정확한 스크롤 위치 계산
      const scrollLeft = index * fullCardWidth;
      
      // 거리에 따라 스크롤 동작 조정
      cardContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: distance > 1 ? 'auto' : 'smooth' // 멀리 이동할 때는 애니메이션 없이 바로 이동
      });
      
      // 멀리 이동하는 경우 약간의 지연 후 자연스러운 효과 적용
      if (distance > 1) {
        setTimeout(() => {
          // 위치 미세 조정으로 자연스러운 효과 주기 
          cardContainerRef.current?.scrollTo({
            left: scrollLeft + 0.1, // 미세한 값 변경으로 리렌더링 트리거
            behavior: 'auto'
          });
        }, 50);
      }
    }
  };
  
  // 스크롤 이벤트 처리
  useEffect(() => {
    const container = cardContainerRef.current;
    if (!container) return;
    
    let scrollTimeout: NodeJS.Timeout;
    let lastKnownScrollLeft = -1;
    
    const handleScroll = () => {
      if (container) {
        // 마지막 스크롤 위치 저장
        lastKnownScrollLeft = container.scrollLeft;
        
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        
        scrollTimeout = setTimeout(() => {
          // 카드 너비 + 마진 오른쪽(20px)을 포함한 계산
          const cardWidth = 335; // 카드 너비 고정
          const cardMargin = 20; // 오른쪽 마진
          const fullCardWidth = cardWidth + cardMargin;
          
          const scrollLeft = lastKnownScrollLeft;
          
          // 사용자가 스와이프한 거리에 따라 다르게 처리
          let newIndex;
          
          // 현재 스크롤 위치가 정확히 카드 경계에 있는지 확인
          const exactPosition = scrollLeft / fullCardWidth;
          const decimalPart = exactPosition - Math.floor(exactPosition);
          
          // 이전 슬라이드와 현재 슬라이드의 스크롤 차이 계산
          const prevScrollLeft = currentSlide * fullCardWidth;
          const scrollDiff = Math.abs(scrollLeft - prevScrollLeft);
          
          // 슬라이드 방향 결정
          const isScrollingRight = scrollLeft > prevScrollLeft;
          
          // 짧은 슬라이드와 긴 슬라이드 구분
          const isLongScroll = scrollDiff > fullCardWidth * 0.5;
          
          if (isLongScroll) {
            // 긴 스크롤의 경우 가장 가까운 카드 위치로 자연스럽게 이동
            if (decimalPart < 0.3) {
              // 30% 미만으로 넘어갔으면 이전 카드로
              newIndex = Math.floor(exactPosition);
            } else if (decimalPart > 0.7) {
              // 70% 이상 넘어갔으면 다음 카드로
              newIndex = Math.ceil(exactPosition);
            } else {
              // 그 외에는 스크롤 방향에 따라 결정
              newIndex = isScrollingRight ? 
                Math.ceil(exactPosition) : 
                Math.floor(exactPosition);
            }
          } else {
            // 짧은 스크롤의 경우 가장 가까운 카드로
            newIndex = Math.round(scrollLeft / fullCardWidth);
          }
          
          // 범위 체크
          newIndex = Math.max(0, Math.min(newIndex, typeOfWorkCards.length - 1));
          
          if (newIndex !== currentSlide && newIndex >= 0 && newIndex < typeOfWorkCards.length) {
            setCurrentSlide(newIndex);
            setActiveCard(null); // 슬라이드 변경 시 활성화된 카드 초기화
            
            // 스크롤 위치 조정
            container.scrollTo({
              left: newIndex * fullCardWidth,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [currentSlide, typeOfWorkCards.length]);
  
  // 리사이즈 시 카드 위치 조정
  useEffect(() => {
    const handleResize = () => {
      if (isMobile && cardContainerRef.current) {
        // 카드 너비 + 마진 오른쪽(20px)을 포함한 계산
        const cardWidth = 335; // 카드 너비 고정
        const cardMargin = 20; // 오른쪽 마진
        const fullCardWidth = cardWidth + cardMargin;
        
        const scrollLeft = currentSlide * fullCardWidth;
        
        cardContainerRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'auto'
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentSlide, isMobile]);
  
  // 모바일 터치 이벤트 처리
  const handleTouchEnd = () => {
    if (cardContainerRef.current) {
      const container = cardContainerRef.current;
      
      // 카드 너비 + 마진 오른쪽(20px)을 포함한 계산
      const cardWidth = 335; // 카드 너비 고정
      const cardMargin = 20; // 오른쪽 마진
      const fullCardWidth = cardWidth + cardMargin;
      
      const scrollLeft = container.scrollLeft;
      
      // 사용자가 스와이프한 거리에 따라 다르게 처리
      // Math.round 대신 Math.floor 또는 Math.ceil을 사용하여 
      // 슬라이드된 방향에 가까운 카드로 이동
      let targetSlide;
      
      // 현재 스크롤 위치가 정확히 카드 경계에 있는지 확인
      const exactPosition = scrollLeft / fullCardWidth;
      const decimalPart = exactPosition - Math.floor(exactPosition);
      
      // 이전 슬라이드와 현재 슬라이드의 스크롤 차이 계산
      const prevScrollLeft = currentSlide * fullCardWidth;
      const scrollDiff = Math.abs(scrollLeft - prevScrollLeft);
      
      // 슬라이드 방향 결정
      const isScrollingRight = scrollLeft > prevScrollLeft;
      
      // 짧은 슬라이드와 긴 슬라이드 구분
      const isLongSwipe = scrollDiff > fullCardWidth * 0.5;
      
      if (isLongSwipe) {
        // 긴 스와이프의 경우 가장 가까운 카드 위치로 자연스럽게 이동
        if (decimalPart < 0.3) {
          // 30% 미만으로 넘어갔으면 이전 카드로
          targetSlide = Math.floor(exactPosition);
        } else if (decimalPart > 0.7) {
          // 70% 이상 넘어갔으면 다음 카드로
          targetSlide = Math.ceil(exactPosition);
        } else {
          // 그 외에는 스와이프 방향에 따라 결정
          targetSlide = isScrollingRight ? 
            Math.ceil(exactPosition) : 
            Math.floor(exactPosition);
        }
      } else {
        // 짧은 스와이프의 경우 가장 가까운 카드로
        targetSlide = Math.round(scrollLeft / fullCardWidth);
      }
      
      // 범위 체크
      targetSlide = Math.max(0, Math.min(targetSlide, typeOfWorkCards.length - 1));
      
      if (targetSlide !== currentSlide) {
        setCurrentSlide(targetSlide);
        setActiveCard(null);
      }
      
      // 정확한 스크롤 위치로 조정
      container.scrollTo({
        left: targetSlide * fullCardWidth,
        behavior: 'smooth'
      });
    }
  };
  
  // Scope Card 클릭 처리
  const handleScopeCardClick = (index: number) => {
    if (isMobile) {
      if (activeScopeCard === index) {
        setActiveScopeCard(null);
      } else {
        setActiveScopeCard(index);
      }
    }
  };
  
  // Process 슬라이드 변경 및 스크롤 이동 함수 (Type of work 방식 참고)
  const handleProcessSlideChange = (index: number) => {
    setProcessSlide(index);
    if (processContainerRef.current) {
      const containerWidth = processContainerRef.current.clientWidth;
      processContainerRef.current.scrollTo({
        left: index * containerWidth,
        behavior: 'smooth',
      });
    }
  };
  
  // Process 리사이즈 시 위치 조정
  useEffect(() => {
    const handleResize = () => {
      if (isMobile && processContainerRef.current) {
        const containerWidth = processContainerRef.current.clientWidth;
        const scrollLeft = processSlide * containerWidth;
        
        processContainerRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'auto'
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // 초기 로드 시 실행
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [processSlide, isMobile]);
  
  // Process 터치 이벤트 처리
  const handleProcessTouchEnd = () => {
    if (processContainerRef.current) {
      const container = processContainerRef.current;
      const containerWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      const targetSlide = Math.round(scrollLeft / containerWidth);
      
      if (targetSlide !== processSlide && targetSlide >= 0 && targetSlide < 3) {
        setProcessSlide(targetSlide);
      }
      
      container.scrollTo({
        left: targetSlide * containerWidth,
        behavior: 'smooth'
      });
    }
  };
  
  // Process 슬라이더 스크롤 시 페이지 인덱스 자동 업데이트 (디바운스 적용)
  const handleProcessSliderScroll = () => {
    if (processContainerRef.current) {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        const container = processContainerRef.current!;
        const containerWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        const newIndex = Math.round(scrollLeft / containerWidth);
        if (newIndex !== processSlide) {
          setProcessSlide(newIndex);
        }
      }, 100); // 100ms 동안 스크롤이 멈추면 페이지 인덱스 업데이트
    }
  };
  
  // Voucher What We Do 항목 클릭 처리
  const handleWhatWeDoClick = (index: number) => {
    setActiveWhatWeDo(index);
  };
  
  // 추가: processSlide가 바뀔 때마다 모바일에서 슬라이더를 부드럽게 이동
  useEffect(() => {
    if (isMobile && processContainerRef.current) {
      const containerWidth = processContainerRef.current.clientWidth;
      const scrollLeft = processSlide * containerWidth;
      processContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [processSlide, isMobile]);

  // 바우처 카드 URL 매핑
  const voucherUrls = [
    'https://www.tourvoucher.or.kr/user/entrprsManage/provdEntrprs/BD_selectProvdEntrprs.do?entrprsId=E202500012&q_rowPerPage=&q_currPage=1&q_sortName=&q_sortOrder=&q_svcSido=&q_svcNm=&q_svcTyCode3=&q_searchVal=%ED%8D%BC%EC%A6%90%EC%BD%94%ED%8D%BC%EB%A0%88%EC%9D%B4%EC%85%98',
    'https://www.mssmiv.com/portal/peform/PeformList',
    'https://www.exportvoucher.com/portal/menupan/menu?totSchInput=%ED%8D%BC%EC%A6%90%EC%BD%94%ED%8D%BC%EB%A0%88%EC%9D%B4%EC%85%98'
  ];

  // 바우처 카드 클릭 핸들러
  const handleVoucherCardClick = (index: number) => {
    if (isMobile) {
      setActiveVoucherCard(index);
    } else {
      window.open(voucherUrls[index], '_blank');
    }
  };

  // 바우처 카드 터치 종료 핸들러
  const handleVoucherCardTouchEnd = (index: number) => {
    if (isMobile && activeVoucherCard === index) {
      window.open(voucherUrls[index], '_blank');
      setActiveVoucherCard(null);
    }
  };

  return (
    <ServiceRoot style={{ backgroundColor: 'white', color: '#1D1D1D' }}>
      <SEO 
        title="퍼즐코퍼레이션 | Service - 마케팅 & IT 서비스"
        description="풀퍼널 퍼포먼스 마케팅, UGC 마케팅, 콘텐츠/브랜딩, IT 솔루션까지. 업종별·목표별 맞춤형 전략으로 브랜드 성장을 가속화시키는 퍼즐코퍼레이션의 서비스 영역을 확인해보세요."
        keywords="퍼즐코퍼레이션, PUZL, 풀퍼널마케팅, UGC마케팅, 브랜드전략, IT솔루션, 디지털광고, 콘텐츠마케팅"
        ogTitle="퍼즐코퍼레이션 | Service - 마케팅 & IT 서비스"
        ogDescription="풀퍼널 퍼포먼스 마케팅, UGC 마케팅, 콘텐츠/브랜딩, IT 솔루션까지. 업종별·목표별 맞춤형 전략으로 브랜드 성장을 가속화시키는 퍼즐코퍼레이션의 서비스 영역을 확인해보세요."
        ogImage="/images/og-image2.png"
      />
      <ServiceContent>
        <WhatWeDoContainer>
          <WhatWeDoTab>
            <WhatWeDoTabItem 
              active={whatWeDoTab === 0} 
              onClick={() => {
                setWhatWeDoTab(0);
                setSearchParams({ tab: 'puzl' });
              }}
            >
              <span>Puzl</span>
            </WhatWeDoTabItem>
            <WhatWeDoTabItem 
              active={whatWeDoTab === 1} 
              onClick={() => {
                setWhatWeDoTab(1);
                setSearchParams({ tab: 'voucher' });
              }}
            >
              <span>Voucher</span>
            </WhatWeDoTabItem>
          </WhatWeDoTab>
          
          {whatWeDoTab === 0 && (
            <>
              <ScrollAnimation delay={0.1} threshold={0.05}>
                <WhatWeDoTitle>What we do</WhatWeDoTitle>
              </ScrollAnimation>
              <ScrollAnimation delay={0.2} threshold={0.05}>
                <ServicesGrid>
                  <ServiceCard>
                    <ServiceCardHeader>
                      <ServiceNumber>01</ServiceNumber>
                      <ServiceCardTitle>실무 경험 5년차 이상의<br />PRO 마케터가 리딩합니다</ServiceCardTitle>
                    </ServiceCardHeader>
                    <div className="text-content">
                      <ServiceCardDescription>
                        온라인 마케팅의 범위는 너무나 다양하고 브랜드 카테고리, 목표, 컨셉 별로 전략은 달라져야 하기에 
                        다양한 경험치가 있는 PRO 마케터가 제안에 참여합니다.
                      </ServiceCardDescription>
                    </div>
                    <IconContainer>
                      <img src="/images/service/what-we-do/icon-trophy.svg" alt="PRO 마케터" width="82" height="82" />
                    </IconContainer>
                  </ServiceCard>
                  
                  <ServiceCard>
                    <ServiceCardHeader>
                      <ServiceNumber>02</ServiceNumber>
                      <ServiceCardTitle>운영했던 히스토리를 토대로<br />개선점을 찾아냅니다</ServiceCardTitle>
                    </ServiceCardHeader>
                    <div className="text-content">
                      <ServiceCardDescription>
                        진단도 없이 약을 처방하는 약사는 없습니다. 광고 운영도 똑같습니다.
                        운영했던 히스토리를 토대로 어떤 부분이 부족했고 개선이 필요한지에 대해 분석을 해야 
                        유의미한 제안과 미팅이 이루어집니다.
                      </ServiceCardDescription>
                    </div>
                    <IconContainer style={{ marginTop: window.innerWidth <= 768 ? '16px' : '20px' }}>
                      <img src="/images/service/what-we-do/icon-time.svg" alt="히스토리 분석" width="72" height="88" />
                    </IconContainer>
                  </ServiceCard>
                  
                  <ServiceCard>
                    <ServiceCardHeader>
                      <ServiceNumber>03</ServiceNumber>
                      <ServiceCardTitle>목표에 딱 맞는<br />맞춤 제안이 이루어집니다</ServiceCardTitle>
                    </ServiceCardHeader>
                    <div className="text-content">
                      <ServiceCardDescription>
                        일반적인 대행사는 브랜드에 필요한 맞춤 제안이 아닌 짜여진 탬플릿에 맞춰 제안하는 경우가 많습니다. 
                        하지만 PUZL은 브랜드의 목적 달성을 위한 맞춤 제안을 진행합니다.
                      </ServiceCardDescription>
                    </div>
                    <IconContainer style={{ marginTop: window.innerWidth <= 768 ? '16px' : '20px' }}>
                      <img src="/images/service/what-we-do/icon-dart.svg" alt="맞춤 제안" width="81" height="88" />
                    </IconContainer>
                  </ServiceCard>
                </ServicesGrid>
              </ScrollAnimation>
              <ScrollAnimation delay={0.3} threshold={0.05}>
                <WorkSection>
                  <WorkSectionHeader>
                    <TypeOfWorkTitle>Type of work</TypeOfWorkTitle>
                    <CustomLink to="/works" style={{ textDecoration: 'none' }}>
                      <SeeAllButton className="desktop-only">
                        <span>See All Projects</span>
                      </SeeAllButton>
                    </CustomLink>
                  </WorkSectionHeader>

                  {/* PC용 카드 그리드 */}
                  <ServiceCardsGrid>
                    {typeOfWorkCards.map((card, index) => (
                      <TypeOfWorkCard 
                        key={card.id}
                        onClick={() => !isMobile && handleCardClick(index)}
                        onTouchStart={() => isMobile && handleCardClick(index)}
                        className={activeCard === index ? 'active' : ''}
                      >
                        <div className="header-row">
                          <ProjectCountText>{card.projectCount}</ProjectCountText>
                          <div 
                            className="go-to-list-area" 
                            onClick={handleGoToListClick(index)}
                            onTouchStart={handleGoToListClick(index)}
                          >
                            <span className="go-to-list">Go to list</span>
                          </div>
                        </div>
                        <div className="content-wrapper">
                          <img 
                            src={card.image} 
                            alt={card.title} 
                            width="272" 
                            height="208" 
                          />
                          <div 
                            className="content-text"
                            dangerouslySetInnerHTML={{ __html: card.text }}
                          />
                          <CardTitle>{card.title.split(' ').map((word, i) => (
                            <React.Fragment key={i}>
                              {word}
                              {i < card.title.split(' ').length - 1 && <br/>}
                            </React.Fragment>
                          ))}</CardTitle>
                        </div>
                      </TypeOfWorkCard>
                    ))}
                  </ServiceCardsGrid>
                  
                  {/* 모바일용 슬라이더 */}
                  <CardSliderContainer>
                    <CardContainer 
                      ref={cardContainerRef}
                      onTouchEnd={() => {
                        if (activeCard === null) {
                          handleTouchEnd();
                        }
                      }}
                    >
                      {typeOfWorkCards.map((card, index) => (
                        <TypeOfWorkCard 
                          key={card.id}
                          onClick={() => !isMobile && handleCardClick(index)}
                          onTouchStart={() => isMobile && handleCardClick(index)}
                          className={activeCard === index ? 'active' : ''}
                        >
                          <div className="header-row">
                            <ProjectCountText>{card.projectCount}</ProjectCountText>
                            <div 
                              className="go-to-list-area" 
                              onClick={handleGoToListClick(index)}
                              onTouchStart={handleGoToListClick(index)}
                            >
                              <span className="go-to-list">Go to list</span>
                            </div>
                          </div>
                          <div className="content-wrapper">
                            <img 
                              src={card.image} 
                              alt={card.title} 
                              width="272" 
                              height="208" 
                            />
                            <div 
                              className="content-text"
                              dangerouslySetInnerHTML={{ __html: card.text }}
                            />
                            <CardTitle>{card.title.split(' ').map((word, i) => (
                              <React.Fragment key={i}>
                                {word}
                                {i < card.title.split(' ').length - 1 && <br/>}
                              </React.Fragment>
                            ))}</CardTitle>
                          </div>
                        </TypeOfWorkCard>
                      ))}
                    </CardContainer>
                    <CardPageIndicator>
                      {typeOfWorkCards.map((_, index) => (
                        <CardIndicatorDot 
                          key={index} 
                          active={currentSlide === index}
                          onClick={() => handleSlideChange(index)}
                        />
                      ))}
                    </CardPageIndicator>
                  </CardSliderContainer>
                  
                  <CustomLink to="/works" style={{ textDecoration: 'none', alignSelf: 'center', display: 'flex', width: '100%', justifyContent: 'center' }}>
                    <SeeAllButton className="mobile-only">
                      <span>See All Projects</span>
                    </SeeAllButton>
                  </CustomLink>
                </WorkSection>
              </ScrollAnimation>
              <ScrollAnimation delay={0.4} threshold={0.05}>
                <MediaPartnerSection>
                  <MediaPartnerContent>
                    <TitleContainer>
                      <MediaPartnerTitle>Media Partner</MediaPartnerTitle>
                      <MediaPartnerDescription>
                        퍼즐코퍼레이션은 다양한 글로벌 플랫폼과 함께 하고 있습니다.
                      </MediaPartnerDescription>
                    </TitleContainer>
                    <MediaPartnerImage1 
                      src={isMobile ? "/images/service/media-partner/logo-mobile-1.png" : "/images/service/media-partner/logo-pc.png"} 
                      alt="Media Partner Logos"
                    />
                    {isMobile && (
                      <MediaPartnerDescription style={{ marginTop: '30px' }}>
                        다양한 글로벌 플랫폼과 함께하여 브랜드 목적에 맞는 캠페인 운영이 가능합니다.
                      </MediaPartnerDescription>
                    )}
                    <div style={isMobile ? { width: '100vw', marginLeft: '-20px', position: 'relative' } : {}}>
                      <MediaPartnerImage2 
                        src="/images/service/media-partner/partner-image.png" 
                        alt="Media Partner Details"
                        style={window.innerWidth <= 1050 ? { width: '100%', marginLeft: '0' } : {}}
                      />
                    </div>
                  </MediaPartnerContent>
                </MediaPartnerSection>
              </ScrollAnimation>
              <ScrollAnimation delay={0.5} threshold={0.05}>
                <ScopeOfWorkSection>
                  <ScopeOfWorkTitle>Scope of Work</ScopeOfWorkTitle>
                  <ScopeOfWorkGrid>
                    <ScopeCard 
                      $isWide 
                      onClick={() => !isMobile && handleScopeCardClick(0)}
                      onTouchStart={() => isMobile && handleScopeCardClick(0)}
                      className={activeScopeCard === 0 ? 'active' : ''}
                    >
                      <ScopeCardTitle className="scope-card-title">Search AD</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-1.svg" alt="Search AD"/>
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>브랜드의 제품 등 관련 키워드를 검색 시 노출되는 광고 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(1)}
                      onTouchStart={() => isMobile && handleScopeCardClick(1)}
                      className={activeScopeCard === 1 ? 'active' : ''}
                    >
                      <ScopeCardTitle className="scope-card-title">Display AD</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-2.svg" alt="Display AD"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>다양한 매체에 타겟팅하여 보여주고 싶은 이미지/영상을 노출시키는 광고</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(2)}
                      onTouchStart={() => isMobile && handleScopeCardClick(2)}
                      className={activeScopeCard === 2 ? 'active multiline-title' : 'multiline-title'}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">Short Form<br/>AD</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">Short Form AD</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-3.svg" alt="Short Form AD"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>30초 이내의 짧은 영상 콘텐츠를 가지고 광고하는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(3)}
                      onTouchStart={() => isMobile && handleScopeCardClick(3)}
                      className={activeScopeCard === 3 ? 'active' : ''}
                    >
                      <ScopeCardTitle className="scope-card-title">Influencer</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-4.svg" alt="Influencer"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>유튜버, 인플루언서 등 다수의 팔로워를 보유한 크리에이터와 콘텐츠를 기획하여 발행하는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(4)}
                      onTouchStart={() => isMobile && handleScopeCardClick(4)}
                      className={activeScopeCard === 4 ? 'active multiline-title' : 'multiline-title'}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">SNS<br/>Power Page</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">SNS Power Page</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-5.svg" alt="SNS Power Page"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>다수의 팔로워를 보유한 SNS 채널에 콘텐츠를 발행하는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(5)}
                      onTouchStart={() => isMobile && handleScopeCardClick(5)}
                      className={activeScopeCard === 5 ? 'active multiline-title' : 'multiline-title'}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">Community Viral</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">Community<br />Viral</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-6.svg" alt="Community Viral"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>다수의 크리에이터를 보유한 커뮤니티 채널에 소비자 사이에서 입소문을 통해 브랜드나 제품, 서비스 콘텐츠를 발행하는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(6)}
                      onTouchStart={() => isMobile && handleScopeCardClick(6)}
                      className={activeScopeCard === 6 ? 'active multiline-title' : 'multiline-title'}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">App<br/>Marketing</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">App Marketing</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-7.svg" alt="App Marketing"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>앱 설치 및 앱 내 이벤트 참여를 유도하는 광고 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(7)}
                      onTouchStart={() => isMobile && handleScopeCardClick(7)}
                      className={activeScopeCard === 7 ? 'active multiline-title' : 'multiline-title'}
                    >
                      <ScopeCardTitle className="scope-card-title">Network AD</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-8.svg" alt="Network AD"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>다양한 포털 및 제휴 매체에 이미지/영상을 노출시키는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      onClick={() => !isMobile && handleScopeCardClick(8)}
                      onTouchStart={() => isMobile && handleScopeCardClick(8)}
                      className={activeScopeCard === 8 ? 'active' : ''}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">ATL</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">ATL</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-9.svg" alt="ATL"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>TV, 잡지, 라디오, 신문 등 4대 매체를 통해 광고를 노출시키는 형태</p>
                      </div>
                    </ScopeCard>
                    <ScopeCard 
                      $isWide 
                      onClick={() => !isMobile && handleScopeCardClick(9)}
                      onTouchStart={() => isMobile && handleScopeCardClick(9)}
                      className={activeScopeCard === 9 ? 'active' : ''}
                    >
                      <ScopeCardTitle className="scope-card-title pc-title">OOH</ScopeCardTitle>
                      <ScopeCardTitle className="scope-card-title mobile-title">OOH</ScopeCardTitle>
                      <div className="title-description-gap"></div>
                      <ScopeImageContainer className="scope-image-container">
                        <img src="/images/service/scope-of-work/card-10.svg" alt="OOH"   />
                      </ScopeImageContainer>
                      <div className="scope-description">
                        <p>지하철, 버스, 건물 등 외장 기반의 광고를 노출시키는 형태</p>
                      </div>
                    </ScopeCard>
                  </ScopeOfWorkGrid>
                </ScopeOfWorkSection>
              </ScrollAnimation>
            </>
          )}

          {whatWeDoTab === 1 && (
            <div className="voucher-container">
              <ScrollAnimation delay={0.1} threshold={0.05}>
                <div className="voucher-header">
                  <h2 className="voucher-title">
                    {isMobile ? (
                      <>
                        바우처 전문 수행기관<br/>
                        퍼즐코퍼레이션에서 성공적인<br/>
                        2025년 <span className="highlight">국가 지원사업</span>을<br/>
                        시작하세요
                      </>
                    ) : (
                      <>
                        바우처 전문 수행기관 퍼즐코퍼레이션에서<br/>
                        성공적인 2025년 <span className="highlight">국가 지원사업</span>을 시작하세요
                      </>
                    )}
                  </h2>
                </div>
              </ScrollAnimation>

              <div style={isMobile ? { marginTop: '-30px' } : undefined}>
                <ScrollAnimation delay={0.2} threshold={0.05}>
                  <div className="voucher-cards-grid">
                    <div 
                      className={`voucher-card ${activeVoucherCard === 0 ? 'active' : ''}`}
                      onClick={() => !isMobile && handleVoucherCardClick(0)}
                      onTouchStart={() => isMobile && handleVoucherCardClick(0)}
                      onTouchEnd={() => handleVoucherCardTouchEnd(0)}
                    >
                      <p>관광기업<br/>혁신바우처사업</p>
                      <div className="icon-container">
                        <img src="/images/service/voucher-card/icon-tour.svg" alt="관광기업 혁신바우처사업" />
                      </div>
                    </div>
                    <div 
                      className={`voucher-card ${activeVoucherCard === 1 ? 'active' : ''}`}
                      onClick={() => !isMobile && handleVoucherCardClick(1)}
                      onTouchStart={() => isMobile && handleVoucherCardClick(1)}
                      onTouchEnd={() => handleVoucherCardTouchEnd(1)}
                    >
                      <p>중소기업<br/>혁신바우처사업</p>
                      <div className="icon-container">
                        <img src="/images/service/voucher-card/icon-business.svg" alt="중소기업 혁신바우처사업" />
                      </div>
                    </div>
                    <div 
                      className={`voucher-card ${activeVoucherCard === 2 ? 'active' : ''}`}
                      onClick={() => !isMobile && handleVoucherCardClick(2)}
                      onTouchStart={() => isMobile && handleVoucherCardClick(2)}
                      onTouchEnd={() => handleVoucherCardTouchEnd(2)}
                    >
                      <p>수출기업<br/>기반바우처사업</p>
                      <div className="icon-container">
                        <img src="/images/service/voucher-card/icon-ship.svg" alt="수출기업 기반바우처사업" />
                      </div>
                    </div>
                  </div>
                </ScrollAnimation>

                <div style={isMobile ? { marginTop: '30px' } : { marginTop: '50px' }}>
                  <ScrollAnimation delay={0.3} threshold={0.05}>
                    <ServiceDescription>
                      {isMobile ? (
                        "퍼즐코퍼레이션의 목표는 클라이언트의 매출 성장입니다. 쇼핑몰부터 스타트업, 대기업까지 다양한 인사이트를 바탕으로 스케일업에 필요한 맞춤형 전략을 제공합니다."
                      ) : (
                        <>
                          퍼즐코퍼레이션의 목표는 클라이언트의 매출 성장입니다.<br />
                          쇼핑몰부터 스타트업, 대기업까지 다양한 인사이트를 바탕으로<br />
                          스케일업에 필요한 맞춤형 전략을 제공합니다.
                        </>
                      )}
                    </ServiceDescription>
                  </ScrollAnimation>
                </div>
              </div>

              <ScrollAnimation delay={0.4} threshold={0.05}>
                <div className="process-section">
                  <div className="process-inner">
                    <h2 className="process-title">Process</h2>
                    
                    {/* Desktop Version */}
                    {!isMobile && (
                      <div className="process-steps-container">
                        <div className="process-step">
                          <span className="process-step-label">STEP 1</span>
                          <div className="process-content">
                            <div className="process-item">
                              <span>서비스 신청</span>
                            </div>
                            <div className="process-item">
                              <span>{isMobile ? "브랜드 분석 및 제안" : <>브랜드 분석<br/>및 제안</>}</span>
                            </div>
                          </div>
                          <span className="process-label">Proposal</span>
                        </div>

                        <div className="process-step">
                          <span className="process-step-label">STEP 2</span>
                          <div className="process-content">
                            <div className="process-item">
                              <span>계약서 작성 및<br/>관리기간 검수</span>
                            </div>
                            <div className="process-item">
                              <span>제안 내용 진행</span>
                            </div>
                          </div>
                          <span className="process-label">Co-Working</span>
                        </div>

                        <div className="process-step">
                          <span className="process-step-label">STEP 3</span>
                          <div className="process-content">
                            <div className="process-item">
                              <span>결과 보고서<br/>전달</span>
                            </div>
                          </div>
                          <span className="process-label">Report</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Mobile Version - Slider */}
                    {isMobile && (
                      <>
                        <div 
                          className="process-slider-container" 
                          ref={processContainerRef}
                          style={{
                            display: 'flex',
                            scrollSnapType: 'x mandatory',
                            width: '100%'
                          }}
                          onScroll={handleProcessSliderScroll}
                        >
                          {[0, 1, 2].map((slideIndex) => (
                            <div 
                              key={slideIndex}
                              className="process-slide"
                              style={{
                                flex: '0 0 100%',
                                width: '100%',
                                scrollSnapAlign: 'start'
                              }}
                            >
                              <div className="step-label-container">
                                <span className="process-step-label">STEP {slideIndex + 1}</span>
                                <span className="process-label">
                                  {slideIndex === 0 ? 'Proposal' : slideIndex === 1 ? 'Co-Working' : 'Report'}
                                </span>
                              </div>
                              <div className="process-content">
                                {slideIndex === 0 && (
                                  <>
                                    <div className="process-item">
                                      <span>서비스 신청</span>
                                    </div>
                                    <div className="process-item">
                                      <span>브랜드 분석 및 제안</span>
                                    </div>
                                  </>
                                )}
                                {slideIndex === 1 && (
                                  <>
                                    <div className="process-item">
                                      <span>계약서 작성 및<br/>관리기간 검수</span>
                                    </div>
                                    <div className="process-item">
                                      <span>제안 내용 진행</span>
                                    </div>
                                  </>
                                )}
                                {slideIndex === 2 && (
                                  <div className="process-item">
                                    <span>결과 보고서<br/>전달</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="process-pagination">
                          {[0, 1, 2].map((index) => (
                            <div 
                              key={index} 
                              className={`process-pagination-indicator ${processSlide === index ? 'active' : ''}`}
                              onClick={() => handleProcessSlideChange(index)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={0.5} threshold={0.05}>
                <div className="voucher-what-we-do-section">
                  <h2 className="voucher-what-we-do-title">What we do</h2>
                  <div className="voucher-what-we-do-container">
                    <div className="voucher-what-we-do-list">
                      <div 
                        className={`voucher-what-we-do-item ${activeWhatWeDo === 0 ? 'active' : ''}`}
                        onClick={() => handleWhatWeDoClick(0)}
                      >
                        <div className="voucher-what-we-do-item-title">
                          <span className="voucher-number">01</span>
                          <span className="voucher-heading">Performance Marketing</span>
                        </div>
                        <VoucherWhatWeDoDescription>
                          {'데이터 기반의 브랜드 분석 및 맞춤 전략으로 성공적인 퍼포먼스를 만드는 서비스'}
                        </VoucherWhatWeDoDescription>
                        
                        {/* 모바일에서만 표시되는 펼쳐진 내용 */}
                        {isMobile && (
                          <div className="mobile-what-we-do-content" style={{ marginTop: '40px', width: '100%' }}>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/main.jpeg" alt="Performance Marketing" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>퍼포먼스 마케팅 전략 제안</h3>
                                <VoucherWhatWeDoDescription>
                                    광고 목표에 가장 빠르게 다가갈 수 있도록 사전에 고객사와 충분한 미팅 및 사전 자료 조사를 통해 캠페인 전략을 제시합니다. 실제 광고 집행 성과를 바탕으로 광고 소재를 유기적으로 수정하고 광고 세팅을 최적화 시킵니다. 쇼핑몰부터 스타트업, 대기업까지 다양한 인사이트를 바탕으로 스케일업에 필요한 맞춤형 전략을 제공합니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=GrowthMarketing" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  퍼포먼스 마케팅 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="voucher-what-we-do-divider"></div>
                      
                      <div 
                        className={`voucher-what-we-do-item ${activeWhatWeDo === 1 ? 'active' : ''}`}
                        onClick={() => handleWhatWeDoClick(1)}
                      >
                        <div className="voucher-what-we-do-item-title">
                          <span className="voucher-number">02</span>
                          <span className="voucher-heading">UGC Marketing</span>
                        </div>
                        <VoucherWhatWeDoDescription>
                          {'소비자의 경험을 통해 확산하는 콘텐츠를 만드는 UGC 마케팅 서비스'}
                        </VoucherWhatWeDoDescription>
                        
                        {/* 모바일에서만 표시되는 펼쳐진 내용 */}
                        {isMobile && (
                          <div className="mobile-what-we-do-content" style={{ marginTop: '40px', width: '100%' }}>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/sns.jpg" alt="UGC Marketing" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>UGC 마케팅 전략 제안</h3>

                                <VoucherWhatWeDoDescription>
                                    소비자 추천보다 좋은 마케팅은 없습니다. 브랜드의 상품 적합성, 주요 키워드, 구매결정요인 별 시장경쟁력을 파악하여 최적의 UGC 마케팅 전략을 수립합니다. 진행영역으로는 MCN, 인플루언서, 블로그, 카페 등에 대한 서비스를 제공하며, 이로인해 생긴 콘텐츠는 2차 마케팅으로 활용하게 됩니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=UGCMarketing" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  UGC 마케팅 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="voucher-what-we-do-divider"></div>
                      
                      <div 
                        className={`voucher-what-we-do-item ${activeWhatWeDo === 2 ? 'active' : ''}`}
                        onClick={() => handleWhatWeDoClick(2)}
                      >
                        <div className="voucher-what-we-do-item-title">
                          <span className="voucher-number">03</span>
                          <span className="voucher-heading">Brand Design</span>
                        </div>
                        <VoucherWhatWeDoDescription>
                          {'CI,BI 패키지 디자인 및 이미지/영상 콘텐츠 제작 서비스'}
                        </VoucherWhatWeDoDescription>
                        
                        {/* 모바일에서만 표시되는 펼쳐진 내용 */}
                        {isMobile && (
                          <div className="mobile-what-we-do-content" style={{ marginTop: '40px', width: '100%' }}>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/design.jpg" alt="Brand Design" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>브랜드 디자인 전략 제안</h3>
                                <VoucherWhatWeDoDescription>
                                    브랜드스토리, 키메시지, 키비주얼, 로고 등 시장분석과 브랜드 이해를 통해 시장에서 경쟁력 있는 BI/CI를 도출해 냅니다. 광고 콘텐츠의 경우 단순히 예쁜 콘텐츠가 아닌 캠페인 목적에 맞는 콘텐츠를 생산합니다. 그렇게 하기 위해 사전에 브랜드의 핵심 가치, 타깃, 소비자에게 보여줘야 하는 콘셉트와 이미지, 메시지, 톤앤매너 등 충분히 고민 후 기획에 수립합니다. 또한 수십만 이상의 파워 페이지 및 채널을 키웠던 에디터들이 직접 기획 및 콘텐츠 제작에 참여합니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=Contents" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  브랜드 디자인 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* PC 버전에서만 표시되는 내용 */}
                    {!isMobile && (
                      <div className="voucher-what-we-do-content">
                        {activeWhatWeDo === 0 && (
                          <>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/main.jpeg" alt="Performance Marketing" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>퍼포먼스 마케팅 전략 제안</h3>
                                <VoucherWhatWeDoDescription>
                                    광고 목표에 가장 빠르게 다가갈 수 있도록 사전에 고객사와 충분한 미팅 및 사전 자료 조사를 통해 캠페인 전략을 제시합니다. 실제 광고 집행 성과를 바탕으로 광고 소재를 유기적으로 수정하고 광고 세팅을 최적화 시킵니다. 쇼핑몰부터 스타트업, 대기업까지 다양한 인사이트를 바탕으로 스케일업에 필요한 맞춤형 전략을 제공합니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=GrowthMarketing" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  퍼포먼스 마케팅 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </>
                        )}
                        
                        {activeWhatWeDo === 1 && (
                          <>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/sns.jpg" alt="UGC Marketing" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>UGC 마케팅 전략 제안</h3>
                                <VoucherWhatWeDoDescription>
                                    소비자 추천보다 좋은 마케팅은 없습니다. 브랜드의 상품 적합성, 주요 키워드, 구매결정요인 별 시장경쟁력을 파악하여 최적의 UGC 마케팅 전략을 수립합니다. 진행영역으로는 MCN, 인플루언서, 블로그, 카페 등에 대한 서비스를 제공하며, 이로인해 생긴 콘텐츠는 2차 마케팅으로 활용하게 됩니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=UGCMarketing" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  UGC 마케팅 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </>
                        )}
                        
                        {activeWhatWeDo === 2 && (
                          <>
                            <img className="voucher-what-we-do-image" src="/images/service/voucher-what-we-do/design.jpg" alt="Brand Design" />
                            <div className="voucher-what-we-do-bottom">
                              <div className="voucher-what-we-do-text">
                                <h3>브랜드 디자인 전략 제안</h3>
                                <VoucherWhatWeDoDescription>
                                    브랜드스토리, 키메시지, 키비주얼, 로고 등 시장분석과 브랜드 이해를 통해 시장에서 경쟁력 있는 BI/CI를 도출해 냅니다. 광고 콘텐츠의 경우 단순히 예쁜 콘텐츠가 아닌 캠페인 목적에 맞는 콘텐츠를 생산합니다. 그렇게 하기 위해 사전에 브랜드의 핵심 가치, 타깃, 소비자에게 보여줘야 하는 콘셉트와 이미지, 메시지, 톤앤매너 등 충분히 고민 후 기획에 수립합니다. 또한 수십만 이상의 파워 페이지 및 채널을 키웠던 에디터들이 직접 기획 및 콘텐츠 제작에 참여합니다.
                                </VoucherWhatWeDoDescription>
                              </div>
                              <button className="voucher-what-we-do-button">
                                <CustomLink to="/works?filter=Contents" style={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                                  브랜드 디자인 사례 보기
                                </CustomLink>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          )}
        </WhatWeDoContainer>
      </ServiceContent>
    </ServiceRoot>
  );
};

export default Service;
